import { useState } from 'react';

import { ChartType } from 'types/dashboard_widget';
import { IconType } from 'types/icon';

import { Icon } from 'components/Icon/IconDeprecated';

type ChartTypeOption = {
  value: ChartType;
  label: string;
  icon: IconType;
};

const TIMESERIES_OPTIONS: ChartTypeOption[] = [
  { value: 'timeseries-bar-grouped', label: 'Grouped bars', icon: 'timeseries-bar-grouped' },
  { value: 'timeseries-bar-stacked', label: 'Stacked bars', icon: 'timeseries-bar-stacked' },
  { value: 'timeseries-heatmap', label: 'Heatmap', icon: 'timeseries-heatmap' },
  { value: 'leaderboard-table', label: 'Table', icon: 'leaderboard-table' },
];

const TIMESERIES_NO_DIMENSIONS_OPTIONS: ChartTypeOption[] = [
  { value: 'timeseries-bar', label: 'Bar chart', icon: 'timeseries-bar' },
  { value: 'timeseries-line', label: 'Line chart', icon: 'timeseries-line' },
  { value: 'leaderboard-table', label: 'Table', icon: 'leaderboard-table' },
];

const LEADERBOARD_OPTIONS: ChartTypeOption[] = [
  { value: 'leaderboard-bar', label: 'Bar chart', icon: 'leaderboard-bar' },
  { value: 'leaderboard-table', label: 'Table', icon: 'leaderboard-table' },
];

type ChartTypeSwitcherProps = {
  queryType: 'counter' | 'timeseries' | 'leaderboard';
  hasDimensions: boolean;
  currentChartType: ChartType;
  onChartTypeChange: (chartType: ChartType) => void;
};

export function ChartTypeSwitcher({
  queryType,
  hasDimensions,
  currentChartType,
  onChartTypeChange,
}: ChartTypeSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Counter doesn't have chart type options
  if (queryType === 'counter') {
    return null;
  }

  let options: ChartTypeOption[];
  if (queryType === 'timeseries') {
    options = hasDimensions ? TIMESERIES_OPTIONS : TIMESERIES_NO_DIMENSIONS_OPTIONS;
  } else {
    options = LEADERBOARD_OPTIONS;
  }

  const currentOption = options.find((opt) => opt.value === currentChartType);

  return (
    <div className="relative">
      <button
        type="button"
        title={`Chart type: ${currentOption?.label || 'Chart'}`}
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-6 w-6 items-center justify-center rounded text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-neutral-200"
        aria-label="Change chart type"
      >
        <Icon name={currentOption?.icon || 'timeseries-bar'} size="xsmall" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close menu */}
          <div
            className="fixed inset-0 z-[10000]"
            onClick={() => setIsOpen(false)}
          />
          {/* Dropdown menu */}
          <div className="absolute bottom-full right-0 z-[10001] mb-1 w-[140px] rounded-md border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
            <div className="px-2 py-1 text-xs font-medium text-neutral-500">
              Chart type
            </div>
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChartTypeChange(option.value);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm transition-colors ${
                  currentChartType === option.value
                    ? 'bg-neutral-100 text-neutral-900 dark:bg-neutral-700 dark:text-neutral-100'
                    : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-700'
                }`}
              >
                <Icon name={option.icon} size="xsmall" />
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
